(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ffcl-root]';
  const initializedRoots = new WeakSet();


  function ensureNoindex() {
    let robots = document.head.querySelector('meta[name="robots"]');

    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }

    const directives = new Set(
      (robots.content || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
    );

    directives.delete('index');
    directives.delete('follow');
    directives.add('noindex');
    directives.add('nofollow');
    robots.content = Array.from(directives).join(', ');
  }

  function initializeSmoothScroll(root) {
    root.querySelectorAll('[data-ffcl-scroll]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        const target = document.getElementById(href.slice(1));
        if (!target) return;

        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start'
        });

        window.history.replaceState(null, '', href);
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      });
    });
  }

  function initializeSlider(slider) {
    const viewport = slider.querySelector('[data-ffcl-viewport]');
    const slides = Array.from(slider.querySelectorAll('[data-ffcl-slide]'));
    const previous = slider.querySelector('[data-ffcl-prev]');
    const next = slider.querySelector('[data-ffcl-next]');
    const dotsContainer = slider.querySelector('[data-ffcl-dots]');

    if (!viewport || slides.length === 0) return;

    let pageCount = 1;
    let currentPage = 0;
    let pageOffsets = [0];
    let scrollTimer;
    let programmaticTimer;
    let isProgrammaticScroll = false;

    function getPerPage() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const raw = mobile ? slider.dataset.perPageMobile : slider.dataset.perPageDesktop;
      const parsed = Number.parseInt(raw || '1', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    function calculatePageOffsets() {
      const perPage = getPerPage();
      pageCount = Math.max(1, Math.ceil(slides.length / perPage));
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);

      pageOffsets = Array.from({ length: pageCount }, (_, page) => {
        const slide = slides[Math.min(page * perPage, slides.length - 1)];
        return Math.min(maxScroll, Math.max(0, slide ? slide.offsetLeft : 0));
      });
    }

    function pageNearestToScroll() {
      const position = viewport.scrollLeft;
      let nearestPage = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      pageOffsets.forEach((offset, index) => {
        const distance = Math.abs(offset - position);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPage = index;
        }
      });

      return nearestPage;
    }

    function updateControls(syncFromScroll = true) {
      if (syncFromScroll) currentPage = pageNearestToScroll();
      currentPage = Math.min(pageCount - 1, Math.max(0, currentPage));

      if (previous) previous.disabled = currentPage === 0;
      if (next) next.disabled = currentPage >= pageCount - 1;

      if (dotsContainer) {
        dotsContainer.querySelectorAll('.ffcl-slider__dot').forEach((dot, index) => {
          if (index === currentPage) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }

    function finishProgrammaticScroll() {
      window.clearTimeout(programmaticTimer);
      isProgrammaticScroll = false;
      updateControls(true);
    }

    function goToPage(page, behavior = 'smooth') {
      const destination = Math.min(pageCount - 1, Math.max(0, page));
      currentPage = destination;
      isProgrammaticScroll = behavior === 'smooth';
      updateControls(false);
      viewport.scrollTo({ left: pageOffsets[destination] || 0, behavior });

      window.clearTimeout(programmaticTimer);
      if (behavior === 'smooth') {
        programmaticTimer = window.setTimeout(finishProgrammaticScroll, 700);
      } else {
        finishProgrammaticScroll();
      }
    }

    function renderDots() {
      calculatePageOffsets();
      currentPage = Math.min(pageCount - 1, currentPage);

      if (dotsContainer) {
        dotsContainer.replaceChildren();
        dotsContainer.hidden = pageCount <= 1;

        for (let index = 0; index < pageCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ffcl-slider__dot';
          dot.setAttribute('aria-label', `${index + 1} / ${pageCount}`);
          dot.addEventListener('click', () => goToPage(index));
          dotsContainer.appendChild(dot);
        }
      }

      if (previous) previous.hidden = pageCount <= 1;
      if (next) next.hidden = pageCount <= 1;
      updateControls(false);
    }

    if (previous) previous.addEventListener('click', () => goToPage(currentPage - 1));
    if (next) next.addEventListener('click', () => goToPage(currentPage + 1));

    viewport.addEventListener(
      'scroll',
      () => {
        if (isProgrammaticScroll) return;
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => updateControls(true), 80);
      },
      { passive: true }
    );

    if ('onscrollend' in window) {
      viewport.addEventListener('scrollend', finishProgrammaticScroll, { passive: true });
    }

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPage(currentPage - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToPage(currentPage + 1);
      }
    });

    renderDots();

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          renderDots();
          goToPage(currentPage, 'auto');
        });
      });
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener('resize', () => {
        renderDots();
        goToPage(currentPage, 'auto');
      }, { passive: true });
    }
  }

  function initializeLightbox(root) {
    const dialog = root.querySelector('[data-ffcl-lightbox]');
    const triggers = Array.from(root.querySelectorAll('[data-ffcl-lightbox-open]'));
    if (!dialog || triggers.length === 0 || typeof dialog.showModal !== 'function') return;

    const image = dialog.querySelector('[data-ffcl-lightbox-image]');
    const caption = dialog.querySelector('[data-ffcl-lightbox-caption]');
    const close = dialog.querySelector('[data-ffcl-lightbox-close]');
    const previous = dialog.querySelector('[data-ffcl-lightbox-prev]');
    const next = dialog.querySelector('[data-ffcl-lightbox-next]');
    let currentIndex = 0;
    let returnFocus = null;
    let previousOverflow = '';

    function renderImage() {
      const trigger = triggers[currentIndex];
      image.src = trigger.dataset.ffclLightboxSrc;
      image.alt = trigger.dataset.ffclLightboxAlt || '';
      const text = trigger.dataset.ffclLightboxCaption || '';
      caption.textContent = text;
      caption.hidden = text.length === 0;
      if (previous) previous.hidden = triggers.length <= 1;
      if (next) next.hidden = triggers.length <= 1;
    }

    function show(index) {
      currentIndex = (index + triggers.length) % triggers.length;
      renderImage();
    }

    function open(index, trigger) {
      currentIndex = index;
      returnFocus = trigger;
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      renderImage();
      dialog.showModal();
      close?.focus();
    }

    function closeDialog() {
      if (dialog.open) dialog.close();
    }

    triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => open(index, trigger));
    });

    close?.addEventListener('click', closeDialog);
    previous?.addEventListener('click', () => show(currentIndex - 1));
    next?.addEventListener('click', () => show(currentIndex + 1));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(currentIndex + 1);
      }
    });

    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = previousOverflow;
      image.removeAttribute('src');
      returnFocus?.focus();
    });
  }


  function initializeRoot(root) {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    if (root.dataset.unsupportedLocale === 'true') {
      ensureNoindex();
      return;
    }

    initializeSmoothScroll(root);
    root.querySelectorAll('[data-ffcl-slider]').forEach(initializeSlider);
    initializeLightbox(root);

  }

  function initialize(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
